import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faBook, 
  faMessage, 
  faPlus, 
  faTrash,
  faCog,
  faServer,
  faFile,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Conversation, Config } from '../../types';
import { deleteChatHistory } from '../../services/api';
import { useAuthToken } from '../../contexts/AuthTokenContext';
import { adminConfig, isAdminUser } from '../../config/adminConfig';
import { SettingsType } from '../Settings/Settings';
import './Sidebar.css';

interface Props {
  showPromptLibrary: boolean;
  isUploading: boolean;
  uploadProgress?: { current: number; total: number };
  showPdfViewer: boolean;
  showSettings: boolean;
  chatHistory: Conversation[];
  currentChatId: string | null;
  config: Config;
  userId: string;
  currentSettingsType?: SettingsType;
  onTogglePromptLibrary: () => void;
  onUploadClick: () => void;
  onTogglePdfVisibility: () => void;
  onNewChat: () => void;
  onLoadConversation: (id: string) => void;
  onOpenSettings: (settingsType: SettingsType) => void;
  onChatHistoryUpdate: () => void;
  onLogout: () => void;
  onBackToChat: () => void;
}

export const Sidebar: React.FC<Props> = ({
  showPromptLibrary,
  isUploading,
  uploadProgress = { current: 0, total: 0 },
  showPdfViewer,
  showSettings,
  chatHistory,
  currentChatId,
  config,
  userId,
  currentSettingsType,
  onTogglePromptLibrary,
  onUploadClick,
  onTogglePdfVisibility,
  onNewChat,
  onLoadConversation,
  onOpenSettings,
  onChatHistoryUpdate,
  onLogout,
  onBackToChat
}) => {
  const { t } = useTranslation();
  const { getToken, account } = useAuthToken();
  const [isDeleting, setIsDeleting] = useState(false);
  const isAdmin = isAdminUser(account?.username || '');
  const canAccessSettings = adminConfig.allowSettingsTabForUsers || isAdmin;
  
  const handleDeleteHistory = async () => {
    if (window.confirm(t('chat.confirmDeleteHistory'))) {
      try {
        setIsDeleting(true);
        const token = await getToken();
        const result = await deleteChatHistory({ 
          token: token || undefined, 
          userId, 
          apiUrl: config.apiUrl 
        });
        
        console.log('Delete result:', result);
        
        // Clear current chat if it exists
        if (currentChatId) {
          onNewChat();
        }
        
        // Update chat history
        onChatHistoryUpdate();
      } catch (error) {
        console.error('Failed to delete chat history:', error);
        alert(t('chat.deleteHistoryError'));
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Render the settings navigation when in settings mode
  const renderSettingsNavigation = () => {
    return (
      <>
        <button className="sidebar-button" onClick={onBackToChat}>
          <FontAwesomeIcon icon={faArrowLeft} /> <span>{t('settings.backToChat')}</span>
        </button>
        
        <div className="sidebar-spacer"></div>
        
        {canAccessSettings && (
          <>
            <div className="sidebar-section">
              <h3 className="sidebar-section-header">{t('settings.title')}</h3>
              <button 
                className={`sidebar-button ${currentSettingsType === 'api' ? 'active' : ''}`} 
                onClick={() => onOpenSettings('api')}
              >
                <FontAwesomeIcon icon={faServer} /> <span>{t('settings.apiSettings')}</span>
              </button>
            </div>
            
            <div className="sidebar-section">
              <h3 className="sidebar-section-header">{t('settings.indexedContent')}</h3>
              <button 
                className={`sidebar-button ${currentSettingsType === 'all-documents' ? 'active' : ''}`} 
                onClick={() => onOpenSettings('all-documents')}
              >
                <FontAwesomeIcon icon={faCog} /> <span>{t('settings.allDocuments')}</span>
              </button>
              <button 
                className={`sidebar-button ${currentSettingsType === 'documents' ? 'active' : ''}`} 
                onClick={() => onOpenSettings('documents')}
              >
                <FontAwesomeIcon icon={faFile} /> <span>{t('settings.documents')}</span>
              </button>
              <button 
                className={`sidebar-button ${currentSettingsType === 'websites' ? 'active' : ''}`} 
                onClick={() => onOpenSettings('websites')}
              >
                <FontAwesomeIcon icon={faGlobe} /> <span>{t('settings.websites')}</span>
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="sidebar">
      <div className="logo-container">
        <img src="/assets/logo-white.png" alt="Logo" className="sidebar-logo" />
      </div>

      {showPdfViewer ? (
        <button className="sidebar-button" onClick={onTogglePdfVisibility}>
          <FontAwesomeIcon icon={faArrowLeft} /> <span>{t('pdf.backToChat')}</span>
        </button>
      ) : showPromptLibrary ? (
        <button className="sidebar-button" onClick={onTogglePromptLibrary}>
          <FontAwesomeIcon icon={faArrowLeft} /> <span>{t('library.backToChat')}</span>
        </button>
      ) : showSettings ? (
        renderSettingsNavigation()
      ) : (
        <>
          <div>
            <button className="sidebar-button" onClick={onTogglePromptLibrary}>
              <FontAwesomeIcon icon={faBook} /> <span>{t('chat.promptLibrary')}</span>
            </button>
          </div>

          <div className="chat-history">
            <div className="chat-header">
              <h3>{t('chat.chatHistory')}</h3>
              <div className="chat-header-actions">
                <button className="icon-button" onClick={onNewChat}>
                  <FontAwesomeIcon icon={faPlus} />
                </button>
                {chatHistory.length > 0 && (
                  <button 
                    className="icon-button" 
                    onClick={handleDeleteHistory}
                    disabled={isDeleting}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
            
            <div className="chat-list">
              {chatHistory.map(chat => (
                <button
                  key={chat.id}
                  className={`chat-item ${currentChatId === chat.id ? 'active' : ''}`}
                  onClick={() => onLoadConversation(chat.id)}
                >
                  <FontAwesomeIcon icon={faMessage} />
                  <span>{chat.title}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 